import { Button, Menu, MenuOpenChangeData, MenuOpenEvent, MenuPopover, MenuTrigger, PositioningShorthand, useOverflowMenu } from "@fluentui/react-components";
import { M365PulseLink, M365PulseSearchBox, SearchResultItem } from "Shared/Components";
import React, { useCallback, useMemo, useState } from "react";
import { observer, useLocalObservable } from "mobx-react";

import { AppLeftBarItem as IAppLeftBarItem } from "../AppLeftBarMenuItems";
import { LeftBarMatchItem } from "../useCheckMatchedItem";
import { OverflowFlyout } from "./OverflowFlyout";
import { OverflowMenuItem } from "./OverflowMenuItem";
import { useAppLeftBarItemStyles } from "../AppLeftBarItem/AppLeftBarItem.styles";
import { useOverflowMenuStyles } from "./OverflowMenu.styles";
import { useSideBarItems } from "../useSideBarItems";
import { useSmallScreen } from "../../../../Shared/Hooks/Core/useSmallScreen";

const emptySearchResult = new Array<SearchResultItem>();

const OverflowMenuBase = ({ apps, matchedItem, onDismiss }: { apps: IAppLeftBarItem[]; matchedItem: LeftBarMatchItem; onDismiss?: () => void }) => {
    const { ref, isOverflowing } = useOverflowMenu<HTMLButtonElement>();
    const { overflowItem } = useSideBarItems();
    const classnames = useOverflowMenuStyles();
    const isSmallScreen = useSmallScreen();

    const localStore = useLocalObservable(() => ({
        menuOpen: false,
        searchText: "",
        filteredApps: apps
    }));
    const itemClassNames = useAppLeftBarItemStyles({ selected: localStore.menuOpen });

    const [isOpen, setIsOpen] = useState(false);

    const showFlyout = useCallback(() => {
        setIsOpen(true);
    }, []);
    const onDismissFlyout = useCallback(() => {
        setIsOpen(false);
        if (onDismiss) {
            onDismiss();
        }
    }, [onDismiss]);

    const onOpenChange = useCallback(
        (e: MenuOpenEvent, data: MenuOpenChangeData) => {
            localStore.menuOpen = data.open;
        },
        [localStore]
    );

    const onSearchChange = useCallback(
        (keyword: string) => {
            const trimmed = keyword.trim().toLowerCase();
            localStore.searchText = trimmed;
            localStore.filteredApps = trimmed ? apps.filter((app) => app.displayName.toLowerCase().includes(trimmed)) : apps;
        },
        [apps, localStore]
    );

    const onClick = useCallback(() => {
        if (isSmallScreen) {
            showFlyout();
        }
    }, [isSmallScreen, showFlyout]);

    const configPositioning: PositioningShorthand = useMemo(
        () => ({
            autoSize: true
        }),
        []
    );

    return !isOverflowing ? null : (
        <div className={classnames.root}>
            <Menu onOpenChange={onOpenChange} positioning={configPositioning}>
                <MenuTrigger>
                    <Button ref={ref} appearance="transparent" className={classnames.trigger} onClick={onClick} aria-label="More">
                        {overflowItem.regularIcon &&
                            overflowItem.filledIcon &&
                            (localStore.menuOpen ? (
                                <overflowItem.filledIcon className={itemClassNames.leftBarIcon} />
                            ) : (
                                <overflowItem.regularIcon className={itemClassNames.leftBarIcon} />
                            ))}
                        <div className={itemClassNames.leftBarTitle} role="none">
                            {overflowItem.displayName}
                        </div>
                    </Button>
                </MenuTrigger>
                {!isSmallScreen ? (
                    <MenuPopover className={classnames.menu}>
                        <M365PulseSearchBox
                            className={classnames}
                            searchResultList={emptySearchResult}
                            onSearchChange={onSearchChange}
                            placeholder="Find an app"
                        />
                        <div className={classnames.appList}>
                            {localStore.filteredApps.map((app, index) => (
                                <OverflowMenuItem key={index} app={app} />
                            ))}
                        </div>
                        <div className={classnames.footer}>
                            <M365PulseLink path="/apphub" className={classnames.button} as="button">
                                Get more apps
                            </M365PulseLink>
                        </div>
                    </MenuPopover>
                ) : (
                    <div />
                )}
            </Menu>
            <OverflowFlyout showFlyout={isOpen} onDismissCallback={onDismissFlyout} apps={apps} />
        </div>
    );
};

export const OverflowMenu = observer(OverflowMenuBase);
